import * as demoData from '../data/demoData';
import { supabase } from '../lib/supabaseClient';
import * as awsApi from './awsApiService';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';
const isDemoMode =
  (process.env.REACT_APP_MODE || '').trim() === 'demo' ||
  (typeof window !== 'undefined' && window.location.hostname === 'demo.flowrcmdental.com');

const simulateDelay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ============================================
// REAL DATA FETCHING FUNCTIONS (Production Mode)
// ============================================

/**
 * Get real patient data from Supabase or AWS
 */
export const getRealPatients = async (practiceId, options = {}) => {
  // Use AWS API when enabled
  if (useAWS) {
    return awsApi.getPatients(practiceId, options);
  }

  // Fallback to Supabase
  try {
    let query = supabase
      .from('patients')
      .select('*')
      .eq('practice_id', practiceId);

    if (options.limit) {
      query = query.limit(options.limit);
    }
    if (options.search) {
      query = query.or(`first_name.ilike.%${options.search}%,last_name.ilike.%${options.search}%`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [], count: data?.length || 0 };
  } catch (error) {
    console.error('[apiService] Error fetching patients:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get real claims data from Supabase or AWS
 */
export const getRealClaims = async (practiceId, options = {}) => {
  // Use AWS API when enabled
  if (useAWS) {
    return awsApi.getClaims(practiceId, options);
  }

  // Fallback to Supabase
  try {
    let query = supabase
      .from('claims')
      .select('*')
      .eq('practice_id', practiceId);

    if (options.status) {
      query = query.eq('status', options.status);
    }
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [], count: data?.length || 0 };
  } catch (error) {
    console.error('[apiService] Error fetching claims:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get real analytics/metrics from Supabase or AWS
 */
export const getRealAnalytics = async (practiceId) => {
  // Use AWS API when enabled
  if (useAWS) {
    return awsApi.getAnalytics(practiceId);
  }

  // Fallback to Supabase
  try {
    // Get patient count
    const { count: patientCount } = await supabase
      .from('patients')
      .select('*', { count: 'exact', head: true })
      .eq('practice_id', practiceId);

    // Get claims stats
    const { data: claims } = await supabase
      .from('claims')
      .select('claim_status, claim_amount, claim_paid_amount')
      .eq('practice_id', practiceId);

    // Calculate metrics
    const totalClaims = claims?.length || 0;
    const pendingClaims = claims?.filter(c => c.claim_status === 'Pending' || c.claim_status === 'Submitted')?.length || 0;
    const deniedClaims = claims?.filter(c => c.claim_status === 'Denied')?.length || 0;
    const paidClaims = claims?.filter(c => c.claim_status === 'Paid')?.length || 0;

    const totalBilled = claims?.reduce((sum, c) => sum + (parseFloat(c.claim_amount) || 0), 0) || 0;
    const totalPaid = claims?.reduce((sum, c) => sum + (parseFloat(c.claim_paid_amount) || 0), 0) || 0;
    const collectionRate = totalBilled > 0 ? ((totalPaid / totalBilled) * 100).toFixed(1) : 0;
    const denialRate = totalClaims > 0 ? ((deniedClaims / totalClaims) * 100).toFixed(1) : 0;

    return {
      success: true,
      data: {
        patients: {
          total: patientCount || 0
        },
        claims: {
          total: totalClaims,
          pending: pendingClaims,
          denied: deniedClaims,
          paid: paidClaims
        },
        revenue: {
          totalBilled,
          totalPaid,
          collectionRate: parseFloat(collectionRate),
          denialRate: parseFloat(denialRate)
        }
      }
    };
  } catch (error) {
    console.error('[apiService] Error fetching analytics:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Get a specific patient by ID for workflow processing
 */
export const getPatientById = async (patientId, practiceId) => {
  try {
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .eq('id', patientId)
      .eq('practice_id', practiceId)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('[apiService] Error fetching patient:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Get claims for a specific patient
 */
export const getClaimsByPatient = async (patientId, practiceId) => {
  try {
    const { data, error } = await supabase
      .from('claims')
      .select('*')
      .eq('patient_id', patientId)
      .eq('practice_id', practiceId)
      .order('service_date', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('[apiService] Error fetching patient claims:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Save workflow result to database for audit trail
 */
export const saveWorkflowResult = async (practiceId, workflowType, inputData, outputData, userId) => {
  try {
    const { data, error } = await supabase
      .from('audit_logs')
      .insert([{
        practice_id: practiceId,
        user_id: userId,
        action: `workflow_${workflowType}`,
        resource_type: 'workflow',
        details: {
          input: inputData,
          output: outputData,
          timestamp: new Date().toISOString()
        }
      }]);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('[apiService] Error saving workflow result:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// WORKFLOW FUNCTIONS - Supabase Edge Functions or AWS Lambda
// ============================================

// Map edge function names to AWS API functions
const awsEndpointMap = {
  'analyze-denial': awsApi.analyzeDenial,
  'validate-claim': awsApi.validateClaim,
  'generate-appeal': awsApi.generateAppeal,
  'generate-preauth': awsApi.generatePreAuth,
  'medical-coding': awsApi.medicalCoding,
  'patient-engagement': awsApi.patientEngagement,
  'verify-benefits': awsApi.verifyBenefits,
  'send-patient-email': awsApi.sendEmail,
  'ocr-capture': awsApi.captureOCR
};

// Helper to call Supabase Edge Functions or AWS Lambda
const callEdgeFunction = async (functionName, data) => {
  // Use AWS Lambda when enabled
  if (useAWS && awsEndpointMap[functionName]) {
    const result = await awsEndpointMap[functionName](data);
    if (!result.success) {
      throw new Error(result.error || `AWS function ${functionName} failed`);
    }
    return result.data;
  }

  // Fallback to Supabase Edge Functions
  const { data: result, error } = await supabase.functions.invoke(functionName, {
    body: data
  });

  if (error) {
    throw new Error(error.message || `Edge function ${functionName} failed`);
  }

  return result;
};

export const verifyDentalBenefits = async (patientData) => {
  if (isDemoMode) {
    await simulateDelay(1800);
    return {
      success: true,
      data: demoData.demoInsuranceResponse,
      mode: 'demo',
      processingTime: demoData.demoInsuranceResponse.processingTime
    };
  }
  try {
    return await callEdgeFunction('verify-benefits', patientData);
  } catch (error) {
    console.error('[apiService] Benefits verification error:', error);
    throw error;
  }
};

export const analyzeDentalDenial = async (denialData) => {
  if (isDemoMode) {
    await simulateDelay(2400);
    return {
      success: true,
      data: demoData.demoDenialAnalysis,
      mode: 'demo',
      processingTime: demoData.demoDenialAnalysis.processingTime
    };
  }
  try {
    return await callEdgeFunction('analyze-denial', denialData);
  } catch (error) {
    console.error('[apiService] Denial analysis error:', error);
    throw error;
  }
};

export const generateDentalPreAuth = async (authData) => {
  if (isDemoMode) {
    await simulateDelay(2800);
    return {
      success: true,
      data: demoData.demoPreAuth,
      mode: 'demo',
      processingTime: demoData.demoPreAuth.processingTime
    };
  }
  try {
    return await callEdgeFunction('generate-preauth', authData);
  } catch (error) {
    console.error('[apiService] Pre-auth generation error:', error);
    throw error;
  }
};

export const generateDentalCoding = async (clinicalData) => {
  if (isDemoMode) {
    await simulateDelay(2100);
    return {
      success: true,
      data: demoData.demoMedicalCoding,
      mode: 'demo',
      processingTime: demoData.demoMedicalCoding.processingTime
    };
  }
  try {
    return await callEdgeFunction('medical-coding', clinicalData);
  } catch (error) {
    console.error('[apiService] Medical coding error:', error);
    throw error;
  }
};

export const validateDentalClaim = async (claimData) => {
  if (isDemoMode) {
    await simulateDelay(1500);
    return {
      success: true,
      data: demoData.demoClaimValidation,
      mode: 'demo',
      processingTime: demoData.demoClaimValidation.processingTime
    };
  }
  try {
    return await callEdgeFunction('validate-claim', claimData);
  } catch (error) {
    console.error('[apiService] Claim validation error:', error);
    throw error;
  }
};

export const scrubDentalClaim = async (claimData) => {
  if (isDemoMode) {
    await simulateDelay(2000);
    return {
      success: true,
      data: {
        confidence: 94,
        scrubResult: {
          riskScore: 42,
          riskLevel: 'Medium',
          denialProbability: '28%',
          issues: [
            { severity: 'High', code: 'FREQ_LIMIT', title: 'Frequency Limitation', description: 'D1110 (prophylaxis) may have been billed within the past 6 months for this patient. Most payers allow 2 cleanings per year.', fix: 'Verify date of last cleaning. If within 6 months, hold claim or obtain medical necessity documentation.' },
            { severity: 'Medium', code: 'MISSING_TOOTH', title: 'Missing Tooth Clause', description: 'If this tooth was missing before the patient enrolled, MetLife may deny under the missing tooth clause.', fix: 'Confirm tooth was present at time of enrollment. Attach documentation if needed.' }
          ],
          passedChecks: ['CDT code format valid', 'ICD-10 diagnosis code valid', 'Provider NPI format correct', 'Fee within usual range for area'],
          recommendation: 'Verify frequency history before submitting. Medium denial risk.',
          estimatedPayment: '$892',
          commonDenialCodes: ['CO-119', 'CO-97']
        }
      },
      mode: 'demo',
      processingTime: '2.1s'
    };
  }
  try {
    const { scrubClaim } = await import('./awsApiService');
    const result = await scrubClaim(claimData);
    if (!result.success) throw new Error(result.error);
    return { success: true, data: result.data, processingTime: '–' };
  } catch (error) {
    console.error('[apiService] Claim scrub error:', error);
    throw error;
  }
};

export const analyzePatientFinancials = async (patientData) => {
  if (isDemoMode) {
    await simulateDelay(1700);
    return {
      success: true,
      data: demoData.demoPatientFinancials,
      mode: 'demo',
      processingTime: demoData.demoPatientFinancials.processingTime
    };
  }
  try {
    return await callEdgeFunction('patient-engagement', patientData);
  } catch (error) {
    console.error('[apiService] Patient financials error:', error);
    throw error;
  }
};

export const processDentalOCR = async (dataOrFile, documentType = 'Insurance Card', extra = {}) => {
  // Handle both formData object and direct file argument
  let imageFile, docType;
  if (dataOrFile && typeof dataOrFile === 'object' && !(dataOrFile instanceof Blob)) {
    imageFile = dataOrFile.Document || dataOrFile.document;
    docType = dataOrFile.DocumentType || dataOrFile.documentType || documentType;
  } else {
    imageFile = dataOrFile;
    docType = documentType;
  }

  if (isDemoMode) {
    await simulateDelay(1900);
    return {
      success: true,
      data: demoData.demoOCRResult,
      mode: 'demo',
      processingTime: demoData.demoOCRResult.processingTime
    };
  }
  try {
    const base64 = imageFile ? await fileToBase64(imageFile) : null;
    return await callEdgeFunction('ocr-capture', { Document: base64, DocumentType: docType, ...extra });
  } catch (error) {
    console.error('[apiService] OCR processing error:', error);
    throw error;
  }
};

// Helper to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const getAppMode = () => {
  return isDemoMode ? 'demo' : 'production';
};

// ============================================
// PATIENT BILLING EMAIL FUNCTIONS
// ============================================

/**
 * Send a patient billing email (balance reminder, payment confirmation, etc.)
 * @param {Object} emailData - Email parameters
 * @param {string} emailData.emailType - Type: 'balance_reminder' | 'payment_confirmation' | 'statement_ready' | 'custom'
 * @param {string} emailData.patientEmail - Patient's email address
 * @param {string} emailData.patientName - Patient's name
 * @param {string} emailData.practiceName - Practice name
 * @param {string} emailData.practicePhone - Practice phone number
 * @param {string} [emailData.balance] - Current balance (for balance_reminder)
 * @param {string} [emailData.customMessage] - Custom message from AI or user
 * @param {string} [emailData.paymentLink] - Optional payment link
 */
export const sendPatientEmail = async (emailData) => {
  if (isDemoMode) {
    await simulateDelay(800);
    return {
      success: true,
      data: {
        messageId: `DEMO-${Date.now()}`,
        emailSent: false,
        demoMode: true,
        preview: {
          to: emailData.patientEmail,
          subject: `${emailData.practiceName} - Your Account Balance`,
          sentAt: new Date().toISOString()
        }
      },
      mode: 'demo',
      processingTime: '0.8s'
    };
  }
  try {
    return await callEdgeFunction('send-patient-email', emailData);
  } catch (error) {
    console.error('[apiService] Send patient email error:', error);
    throw error;
  }
};

/**
 * Send balance reminder email to a patient
 */
export const sendBalanceReminder = async (patient, practice, customMessage = null) => {
  return sendPatientEmail({
    emailType: 'balance_reminder',
    patientEmail: patient.email,
    patientName: `${patient.first_name} ${patient.last_name}`,
    practiceName: practice.name,
    practicePhone: practice.phone || '',
    practiceEmail: practice.email,
    balance: `$${parseFloat(patient.outstanding_balance || 0).toFixed(2)}`,
    customMessage: customMessage
  });
};

/**
 * Send payment confirmation email
 */
export const sendPaymentConfirmation = async (patient, practice, paymentAmount, remainingBalance = null) => {
  return sendPatientEmail({
    emailType: 'payment_confirmation',
    patientEmail: patient.email,
    patientName: `${patient.first_name} ${patient.last_name}`,
    practiceName: practice.name,
    practicePhone: practice.phone || '',
    paymentAmount: `$${parseFloat(paymentAmount).toFixed(2)}`,
    paymentDate: new Date().toLocaleDateString(),
    remainingBalance: remainingBalance ? `$${parseFloat(remainingBalance).toFixed(2)}` : undefined
  });
};

/**
 * Send AI-generated collection message to patient
 * Integrates with Patient Financial AI workflow
 */
export const sendAIGeneratedEmail = async (patient, practice, aiMessage) => {
  return sendPatientEmail({
    emailType: 'balance_reminder',
    patientEmail: patient.email,
    patientName: `${patient.first_name} ${patient.last_name}`,
    practiceName: practice.name,
    practicePhone: practice.phone || '',
    practiceEmail: practice.email,
    balance: `$${parseFloat(patient.outstanding_balance || 0).toFixed(2)}`,
    customMessage: aiMessage
  });
};
